import React, { memo, useCallback, useEffect, useMemo } from 'react'

import { CabinClass, TripType } from '@/shared/functions/Flights/flights.types'
import { Box, Divider, Typography } from '@mui/material'
import { FormikProps, useFormik } from 'formik'

import FlightSegments from '@/pages/AdminDashboard/components/ExternalBookings/components/FlightSegments/FlightSegments.component'
import Form from '@/shared/components/Form/Form.component'
import Modal from '@/shared/components/Modal/Modal.component'

import {
  flightDetailsFormSchema,
  initialFlightDetailsValues,
  initialTripDetailsValues,
  tripDetailsFormSchema,
  tripDetailsFormSection
} from './ExternalBookingsConfirmationModal.dictionary'
import {
  ExternalBookingsConfirmationModalProps,
  ExtractedSegment,
  FlightDetailsFormValues,
  TripDetailsFormValues
} from './ExternalBookingsConfirmationModal.types'

const ExternalBookingsConfirmationModal: React.FC<ExternalBookingsConfirmationModalProps> = (props) => {
  const { open, onClose, extractedData, onSubmit, airports, airlines, createItineraryLoading, warnings } = props

  // Convert cabin class from API format (camelCase) to form format (snake_case)
  const convertCabinClassToFormFormat = useCallback((apiCabinClass?: string): CabinClass => {
    if (!apiCabinClass) return 'economy'

    // Map from API format to form format
    const cabinClassMap: Record<string, CabinClass> = {
      economy: 'economy',
      premiumEconomy: 'premium_economy',
      premium_economy: 'premium_economy', // Handle both formats
      business: 'business',
      first: 'first'
    }

    return cabinClassMap[apiCabinClass] || 'economy'
  }, [])

  // Map extracted data to form values
  const getInitialTripDetailsValues = useCallback((): TripDetailsFormValues => {
    if (extractedData) {
      return {
        tripType: (extractedData.tripType || 'roundTrip') as TripType,
        cabinClass: convertCabinClassToFormFormat(extractedData.cabinClass),
        tripPrice: extractedData.booking?.payment?.totalPaid?.toString() || '',
        tripCurrency: extractedData.booking?.payment?.currency || 'USD'
      }
    }

    return initialTripDetailsValues
  }, [extractedData, convertCabinClassToFormFormat])

  // Helper to map a segment to form format
  const mapSegmentToFormFormat = useCallback(
    (seg: ExtractedSegment) => {
      const airlineCode = seg.marketingCarrier || seg.operatingCarrier
      const matchingAirline = airlines.find((airline: { value: string; label: string; iataCode?: string }) => {
        // If we have a specific IATA code, only match airlines with that exact IATA code
        if (airlineCode) {
          return airline.iataCode === airlineCode
        }
        // If no IATA code in segment, fallback to checking label
        const labelUpper = airline.label.toUpperCase()

        return labelUpper.includes(airlineCode || '') || airline.value.includes(airlineCode || '')
      })

      return {
        flightNumber: seg.flightNumber || '',
        airlineId: matchingAirline?.value || '',
        departureTime: seg.departure?.datetimeLocal || '',
        arrivalTime: seg.arrival?.datetimeLocal || '',
        originAirportIataCode: seg.departure?.airport || '',
        destinationAirportIataCode: seg.arrival?.airport || ''
      }
    },
    [airlines]
  )

  // Map segments to FlightSegments format
  const getInitialFlightDetailsValues = useCallback((): FlightDetailsFormValues => {
    if (!extractedData?.segments || extractedData.segments.length === 0) {
      return initialFlightDetailsValues
    }

    const tripType = (extractedData.tripType || 'roundTrip') as TripType

    // PRIORITY: Use pre-computed slices from backend if available
    // The backend correctly groups segments using time-gap analysis
    if (extractedData.slices && extractedData.slices.length > 0) {
      const hasSegmentsInSlices = extractedData.slices.every((slice) => slice.segments && slice.segments.length > 0)

      if (hasSegmentsInSlices) {
        const slicesFromBackend: FlightDetailsFormValues['slices'] = extractedData.slices.map((slice) => ({
          originAirportIataCode: slice.origin || slice.segments?.[0]?.departure?.airport || '',
          destinationAirportIataCode:
            slice.destination || slice.segments?.[slice.segments.length - 1]?.arrival?.airport || '',
          travelDate: slice.segments?.[0]?.departure?.datetimeLocal || '',
          segments: (slice.segments || []).map(mapSegmentToFormFormat)
        }))

        return { slices: slicesFromBackend }
      }
    }

    // FALLBACK: Group segments into slices based on trip type (legacy behavior)
    let slices: FlightDetailsFormValues['slices'] = []

    if (tripType === 'roundTrip' && extractedData.segments.length >= 2) {
      // Find the split point using time gaps (same logic as backend rebuildSlicesIfNeeded)
      // The largest gap between arrival and next departure indicates the outbound/return split
      let maxGapIndex = 0
      let maxGapMilliseconds = 0

      for (let index = 0; index < extractedData.segments.length - 1; index++) {
        const currentArrival = extractedData.segments[index]?.arrival?.datetimeLocal
        const nextDeparture = extractedData.segments[index + 1]?.departure?.datetimeLocal

        if (currentArrival && nextDeparture) {
          const arrivalTime = new Date(currentArrival).getTime()
          const departureTime = new Date(nextDeparture).getTime()

          if (!isNaN(arrivalTime) && !isNaN(departureTime)) {
            const gapMilliseconds = departureTime - arrivalTime

            if (gapMilliseconds > maxGapMilliseconds) {
              maxGapMilliseconds = gapMilliseconds
              maxGapIndex = index
            }
          }
        }
      }

      // Split at the largest gap
      const outboundSegments = extractedData.segments.slice(0, maxGapIndex + 1)
      const returnSegments = extractedData.segments.slice(maxGapIndex + 1)

      slices = [
        {
          segments: outboundSegments.map(mapSegmentToFormFormat)
        },
        {
          segments: returnSegments.map(mapSegmentToFormFormat)
        }
      ]
    } else if (tripType === 'oneWay') {
      // All segments in one slice for oneWay
      slices = [
        {
          segments: extractedData.segments.map(mapSegmentToFormFormat)
        }
      ]
    } else if (tripType === 'multiCity') {
      // For multiCity, create one slice per segment (each segment is its own route)
      slices = extractedData.segments.map((seg) => ({
        originAirportIataCode: seg.departure?.airport || '',
        destinationAirportIataCode: seg.arrival?.airport || '',
        travelDate: seg.departure?.datetimeLocal || '',
        segments: [mapSegmentToFormFormat(seg)]
      }))
    } else {
      // Fallback: one slice with all segments
      slices = [
        {
          segments: extractedData.segments.map(mapSegmentToFormFormat)
        }
      ]
    }

    return { slices }
  }, [extractedData, mapSegmentToFormFormat])

  const tripDetailsFormik: FormikProps<TripDetailsFormValues> = useFormik({
    initialValues: getInitialTripDetailsValues(),
    validationSchema: tripDetailsFormSchema,
    enableReinitialize: true,
    onSubmit: () => {
      // Do nothing
    }
  })

  const flightDetailsFormik: FormikProps<FlightDetailsFormValues> = useFormik({
    initialValues: getInitialFlightDetailsValues(),
    validationSchema: flightDetailsFormSchema,
    enableReinitialize: true,
    onSubmit: () => {
      // Do nothing
    }
  })

  useEffect(() => {
    if (extractedData && airlines.length > 0) {
      tripDetailsFormik.setValues(getInitialTripDetailsValues())
      flightDetailsFormik.setValues(getInitialFlightDetailsValues())

      // Trigger validation by focusing and blurring fields after 500ms
      setTimeout(() => {
        // Focus and blur trip price field to trigger validation
        const tripPriceInput = document.querySelector('input[name="tripPrice"]') as HTMLInputElement
        if (tripPriceInput) {
          tripPriceInput.focus()
          tripPriceInput.blur()
        }

        // Focus and blur date fields to trigger validation
        const dateInputs = document.querySelectorAll('input[type="datetime-local"], input[name*="Time"]')
        dateInputs.forEach((input) => {
          ;(input as HTMLInputElement).focus()
          ;(input as HTMLInputElement).blur()
        })
      }, 500)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extractedData, airlines])

  const handleCloseModal = useCallback(() => {
    tripDetailsFormik.resetForm()
    flightDetailsFormik.resetForm()
    onClose()
  }, [onClose, tripDetailsFormik, flightDetailsFormik])

  const handleTripDetailsFormChange = useCallback(
    (formik: Partial<FormikProps<unknown>>) => {
      const currentValues = tripDetailsFormik.values
      const updatedValues = formik.values as TripDetailsFormValues

      if (JSON.stringify(currentValues) !== JSON.stringify(updatedValues)) {
        tripDetailsFormik.setValues(updatedValues)
      }
    },
    [tripDetailsFormik]
  )

  const isFormValid = useCallback(() => {
    // Check trip details form
    const isTripDetailsFormValid =
      tripDetailsFormik.isValid &&
      Object.keys(tripDetailsFormik.errors).length === 0 &&
      tripDetailsFormik.values.tripType &&
      tripDetailsFormik.values.cabinClass &&
      tripDetailsFormik.values.tripPrice &&
      tripDetailsFormik.values.tripCurrency

    // Check flight details form - validate nested segments
    const isFlightDetailsFormValid =
      flightDetailsFormik.isValid &&
      Object.keys(flightDetailsFormik.errors).length === 0 &&
      flightDetailsFormik.values.slices.length > 0 &&
      flightDetailsFormik.values.slices.every(
        (slice) =>
          slice.segments.length > 0 &&
          slice.segments.every(
            (segment) =>
              segment.flightNumber &&
              segment.airlineId &&
              segment.departureTime &&
              segment.arrivalTime &&
              segment.originAirportIataCode &&
              segment.destinationAirportIataCode
          )
      )

    return isTripDetailsFormValid && isFlightDetailsFormValid
  }, [tripDetailsFormik, flightDetailsFormik])

  const handleSubmit = useCallback(() => {
    const formValues = {
      tripType: tripDetailsFormik.values.tripType,
      cabinClass: tripDetailsFormik.values.cabinClass,
      tripPrice: tripDetailsFormik.values.tripPrice,
      tripCurrency: tripDetailsFormik.values.tripCurrency,
      slices: flightDetailsFormik.values.slices.map((slice) => ({
        // Always derive slice origin/dest from segments to reflect user edits
        originAirportIataCode: slice.segments[0]?.originAirportIataCode || slice.originAirportIataCode || '',
        destinationAirportIataCode:
          slice.segments[slice.segments.length - 1]?.destinationAirportIataCode ||
          slice.destinationAirportIataCode ||
          '',
        // Always derive travelDate from first segment's departure time to reflect user edits
        travelDate: slice.segments[0]?.departureTime || slice.travelDate || '',
        segments: slice.segments.map((segment) => ({
          flightNumber: segment.flightNumber,
          airlineId: segment.airlineId,
          departureTime: segment.departureTime,
          arrivalTime: segment.arrivalTime,
          originAirportIataCode: segment.originAirportIataCode,
          destinationAirportIataCode: segment.destinationAirportIataCode
        }))
      }))
    }

    onSubmit(formValues)
  }, [tripDetailsFormik, flightDetailsFormik, onSubmit])

  const getModalHeader = useCallback(() => {
    if (!extractedData) return null

    const rows = [
      {
        label: 'Confirmation Code',
        value: extractedData.recordLocator || 'N/A'
      },
      {
        label: 'Airline/Agency',
        value: extractedData.source?.brand || 'N/A'
      }
    ]

    return (
      <Box sx={{ mb: 2 }}>
        {rows.map((row, index) => (
          <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {row.label}:
            </Typography>

            <Typography variant="body2">{row.value}</Typography>
          </Box>
        ))}
        {warnings && warnings.length > 0 && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              bgcolor: 'warning.light',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'warning.main'
            }}
          >
            <Typography variant="body2" fontWeight={600} color="black" gutterBottom>
              Please review:
            </Typography>

            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {warnings.map((warning: string, idx: number) => (
                <li key={idx}>
                  <Typography variant="body2" color="black">
                    {warning}
                  </Typography>
                </li>
              ))}
            </ul>
          </Box>
        )}
      </Box>
    )
  }, [extractedData, warnings])

  const getModalContent = useCallback(() => {
    if (!extractedData) return null

    return (
      <Box>
        {getModalHeader()}

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mt: 3 }}>
          <Form
            initialValues={getInitialTripDetailsValues()}
            hideButtons
            validationSchema={tripDetailsFormSchema}
            onSubmit={handleSubmit}
            onFormChange={(formik) => handleTripDetailsFormChange(formik)}
            formSections={[tripDetailsFormSection]}
          />

          {flightDetailsFormik && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Flight Details
              </Typography>

              <FlightSegments
                formik={flightDetailsFormik}
                airports={airports}
                airlines={airlines}
                tripType={tripDetailsFormik.values.tripType}
                airlineInBookingRequest={undefined}
              />
            </Box>
          )}
        </Box>
      </Box>
    )
  }, [
    extractedData,
    flightDetailsFormik,
    handleSubmit,
    airports,
    airlines,
    getModalHeader,
    handleTripDetailsFormChange,
    tripDetailsFormik.values.tripType,
    getInitialTripDetailsValues
  ])

  // Check if all departure dates are in the past
  const areAllFlightsPast = useMemo(() => {
    if (!flightDetailsFormik.values.slices || flightDetailsFormik.values.slices.length === 0) {
      return false
    }

    const now = new Date()

    // Check all segments across all slices
    const allSegments = flightDetailsFormik.values.slices.flatMap((slice) => slice.segments || [])

    if (allSegments.length === 0) return false

    // Check if ALL segments have departure times in the past
    const allPast = allSegments.every((segment) => {
      if (!segment.departureTime) return false

      try {
        const departureDate = new Date(segment.departureTime)

        return departureDate < now
      } catch {
        return false
      }
    })

    return allPast && allSegments.length > 0
  }, [flightDetailsFormik.values.slices])

  const getTooltipText = () => {
    if (areAllFlightsPast) {
      return 'Cannot submit itinerary with all flights in the past'
    }

    if (isFormValid()) return undefined

    return 'You must fill in all fields to confirm the booking'
  }

  const getHeaderRightSection = () => {
    // Confidence score removed - not needed for traveler view
    return undefined
  }

  return (
    <Modal
      open={open}
      onClose={handleCloseModal}
      maxWidth="md"
      title="Confirm Extracted Itinerary"
      subtitle="Review and edit the extracted flight details"
      headerRightSection={getHeaderRightSection()}
      body={getModalContent()}
      primaryButton={{
        label: createItineraryLoading ? 'Creating Trip...' : 'Confirm & Create Trip',
        loading: createItineraryLoading,
        tooltipText: getTooltipText(),
        disabled: !isFormValid() || createItineraryLoading || areAllFlightsPast,
        onClick: handleSubmit
      }}
      secondaryButton={{
        label: 'Cancel',
        onClick: handleCloseModal
      }}
    />
  )
}

export default memo(ExternalBookingsConfirmationModal)
